"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.deviceShape = exports.PACKAGE_SHAPE = exports.DEFAULT_OFFER_BLURB = exports.DEFAULT_OFFER_FEATURES = exports.OFFER_TOKENS = void 0;
exports.formatOfferDollars = formatOfferDollars;
exports.offerPriceLabel = offerPriceLabel;
exports.offerTabLabel = offerTabLabel;
exports.offerScopeNote = offerScopeNote;
exports.offerSavingLabel = offerSavingLabel;
exports.offerPromoDurationLabel = offerPromoDurationLabel;
exports.offerDiscountLabel = offerDiscountLabel;
exports.offerInputsFromDeal = offerInputsFromDeal;
exports.packagePriceMap = packagePriceMap;
exports.dealPackageRows = dealPackageRows;
exports.packagePhasedLines = packagePhasedLines;
exports.dealPhaseView = dealPhaseView;
exports.dealTotalsFromRows = dealTotalsFromRows;
exports.buildOfferPresentation = buildOfferPresentation;
const dealOffer_1 = require("./dealOffer");
const pricePhases_1 = require("./pricePhases");
const deal_1 = require("./deal");
const dealDevice_1 = require("./dealDevice");
/**
 * THE ONE OFFER PRESENTATION VOCABULARY.
 *
 * Every price string and every promotional sentence a customer reads about a
 * deal is built here, and nowhere else. Before this module the same constants
 * and formatters were hand-copied between super-admin-ui's `dealPreviewModel`
 * and platform-app's `rubytill-landing/pricing` — two repos with no compiler
 * between them, so every edit to one was a silent divergence. That is what
 * this file exists to make impossible.
 */
/**
 * The deal live view's palette, which the spec makes the reference design.
 * Exported as tokens rather than a component: the surfaces compose their own
 * layouts (the live view has no device selection, an order does), so what they
 * share is the vocabulary, not the markup.
 */
exports.OFFER_TOKENS = {
    ground: '#101013',
    text: '#f4f4f5',
    muted: '#a1a1aa',
    faint: '#6b6b74',
    saving: '#fbbf24',
    accent: '#DC143C',
};
/** Shown when a deal checks no packages at all — never an empty bullet list. */
exports.DEFAULT_OFFER_FEATURES = [
    'Full POS Terminal',
    'Real-time Inventory',
    'Multi-location Support',
    'Financial Reporting',
    'Priority Support',
];
/** Shown when the operator has not typed marketing copy yet. */
exports.DEFAULT_OFFER_BLURB = 'Cancel anytime. No questions asked.';
/**
 * `$29` / `$29.99` — whole dollars render with no trailing `.00`; a non-whole
 * amount keeps its cents.
 */
function formatOfferDollars(cents) {
    const dollars = (cents ?? 0) / 100;
    return Number.isInteger(dollars) ? `$${dollars}` : `$${dollars.toFixed(2)}`;
}
/** Full cents, for figures a customer compares against an invoice. */
function formatOfferMoney(cents) {
    return `$${((cents ?? 0) / 100).toFixed(2)}`;
}
/** The device's price, worded the way the reference card words it. */
function offerPriceLabel(sku, amountCents) {
    switch (sku.saleMode) {
        case 'outright':
            return `${formatOfferDollars(amountCents)} once`;
        case 'installment':
        case 'rental':
        case 'lease':
            return `+${formatOfferDollars(amountCents)}/${sku.interval === 'year' ? 'yr' : 'mo'}`;
        case 'included':
            return 'Included free';
        default:
            return formatOfferDollars(amountCents);
    }
}
/** The chooser button's word for a sale mode. */
function offerTabLabel(saleMode) {
    switch (saleMode) {
        case 'outright': return 'Buy';
        case 'rental': return 'Rent';
        case 'installment': return 'Finance';
        case 'lease': return 'Lease';
        case 'included': return 'Included';
        default: return 'Other';
    }
}
/**
 * The customer-facing sentence for an offer's LIMITS, or null when it has
 * none. Stated only when the offer is genuinely narrower than "every unit,
 * everywhere": a note on an unlimited offer is noise, and noise is what gets
 * skimmed past on the one that does carry a limit.
 */
function offerScopeNote(term) {
    if (!term)
        return null;
    const units = term.covers === 'first_n' && typeof term.coveredUnits === 'number'
        ? `first ${term.coveredUnits} per location`
        : null;
    const locations = typeof term.coveredLocations === 'number'
        ? `first ${term.coveredLocations} location${term.coveredLocations === 1 ? '' : 's'}`
        : null;
    if (!units && !locations)
        return null;
    if (units && locations)
        return `this price covers the ${units}, at your ${locations}`;
    return `this price covers the ${units || locations}`;
}
/**
 * "Save $20.00/mo", or null. Null rather than "Save $0.00" is the whole point:
 * a surface that renders this unconditionally cannot advertise a saving that
 * was never given.
 */
function offerSavingLabel(savingCents) {
    if (!Number.isFinite(savingCents) || savingCents <= 0)
        return null;
    return `Save ${formatOfferMoney(savingCents)}/mo`;
}
/** "for 12 months", or null for an open-ended price. */
function offerPromoDurationLabel(months) {
    if (!months || !Number.isFinite(months) || months <= 0)
        return null;
    return `for ${months} month${months === 1 ? '' : 's'}`;
}
/**
 * "20% off" / "$10.00 off", or null. The operator's own percentage wins when
 * they stated one, because that is the number they wrote the campaign around.
 */
function offerDiscountLabel(args) {
    const { listCents, netCents, percentOff } = args;
    if (!Number.isFinite(listCents) || !Number.isFinite(netCents))
        return null;
    if (netCents >= listCents)
        return null;
    if (typeof percentOff === 'number' && percentOff > 0)
        return `${percentOff}% off`;
    return `${formatOfferMoney(listCents - netCents)} off`;
}
const asRecord = (value) => (value && typeof value === 'object' && !Array.isArray(value)) ? value : {};
const asStringArray = (value) => Array.isArray(value) ? value.filter((v) => typeof v === 'string' && v.length > 0) : [];
/**
 * A stored deal row — with its active published version ALREADY overlaid by
 * `applyDealVersion` — as model inputs.
 *
 * `monthlyAmount` is the fallback for a deal built before `priceCents`
 * existed; the same precedence `effectiveMonthlyAmountCents` uses on the
 * server, so a legacy deal prices identically here and there.
 */
function offerInputsFromDeal(deal) {
    const priceCents = typeof deal?.priceCents === 'number'
        ? deal.priceCents
        : (typeof deal?.monthlyAmount === 'number' ? deal.monthlyAmount : null);
    return {
        ...(deal?.id ? { id: String(deal.id) } : {}),
        code: String(deal?.code || ''),
        name: String(deal?.name || ''),
        priceCents,
        packageIds: asStringArray(deal?.packageIds),
        deviceSkuIds: asStringArray(deal?.deviceSkuIds),
        offerTerms: asRecord(deal?.offerTerms),
        packageDiscounts: asRecord(deal?.packageDiscounts),
        packageIntros: asRecord(deal?.packageIntros),
        deviceTerms: asRecord(deal?.deviceTerms),
        landingPageLabel: String(deal?.landingPageLabel || ''),
        landingPageDescription: String(deal?.landingPageDescription || ''),
        isDefaultDeal: Boolean(deal?.isDefaultDeal),
        trialDays: typeof deal?.trialDays === 'number' && Number.isFinite(deal.trialDays) ? deal.trialDays : 0,
    };
}
/**
 * `{ packageId -> list price }`, built the way the SERVER builds it in
 * `fetchPackagePrices`: a package row that exists but carries no numeric
 * `basePrice` prices at ZERO — it is not "missing". Only a package id with no
 * row at all is missing, and that is a data gap reported by name.
 *
 * Mirroring the server exactly matters more than being clever here: any other
 * rule would make the builder's total and the server's `packagesTotals`
 * disagree for the same deal.
 */
function packagePriceMap(packages) {
    const map = {};
    for (const p of packages || []) {
        if (!p?.id)
            continue;
        map[p.id] = { packageId: p.id, basePriceCents: typeof p.basePrice === 'number' ? p.basePrice : 0 };
    }
    return map;
}
/**
 * WHAT A SOFTWARE PACKAGE IS, as far as an offer is concerned: it bills
 * monthly, has nothing due up front, and there is always exactly one of it.
 * Declared once so every caller asks the same question of the same control.
 */
exports.PACKAGE_SHAPE = { hasMonthly: true, hasOneTime: false, hasQuantity: false };
/** A device finance option: it may bill monthly, may take money up front, and has a quantity. */
const deviceShape = (saleMode) => ({
    hasMonthly: saleMode !== 'outright' && saleMode !== 'included',
    hasOneTime: saleMode !== 'included',
    hasQuantity: true,
});
exports.deviceShape = deviceShape;
/**
 * A stored `Discount` is recognised by carrying AT LEAST ONE of the two amounts
 * `DiscountSchema` allows — a cheap guard in front of the schema, not a
 * restatement of it. A form's blank `{ mode: 'none' }` placeholder carries
 * neither and is therefore not one, and treating it as a discount would mark an
 * undiscounted line as deal-priced.
 */
const storedDiscount = (d) => (d && typeof d === 'object'
    && (typeof d.percentOff === 'number' || typeof d.amountOffCents === 'number')
    ? d
    : undefined);
const storedIntro = (i) => (i && typeof i === 'object'
    && typeof i.introPriceCents === 'number' && typeof i.introMonths === 'number'
    ? i
    : undefined);
function resolveParsers(p) {
    return {
        parseTerm: p?.parseTerm ?? ((t) => (t && typeof t === 'object' ? t : undefined)),
        termError: p?.termError ?? (() => null),
        parseDiscount: p?.parseDiscount ?? storedDiscount,
        discountError: p?.discountError ?? (() => null),
        parseIntro: p?.parseIntro ?? storedIntro,
        introError: p?.introError ?? (() => null),
        termToInput: p?.termToInput ?? ((t) => t),
    };
}
/**
 * One row per package on the deal: list price, the discount as typed, and the
 * net that discount produces.
 *
 * The net is `offerAmounts`' settled monthly — the one pipeline the server
 * bills from — so a row and the total below it can never round apart. A
 * discount the caller's parser refuses yields `undefined`, so the row bills at
 * LIST and `error` says why; it is never quietly rounded into a discount nobody
 * typed.
 */
function dealPackageRows(packageIds, packages, discounts, 
/** Introductory prices, as the caller holds them. Absent for a deal with none. */
intros, 
/** The unified offers. Preferred over `discounts`/`intros` when present. */
offers, 
/** How to parse the three above. Omitted = they are already-parsed stored shapes. */
parsers) {
    const P = resolveParsers(parsers);
    const prices = packagePriceMap(packages);
    const byId = new Map((packages || []).filter((p) => p?.id).map((p) => [p.id, p]));
    return Array.from(new Set(packageIds || [])).map((packageId) => {
        const record = byId.get(packageId);
        const price = prices[packageId];
        const discount = discounts?.[packageId] || { mode: 'none', value: '', reason: '' };
        const listCents = price ? Math.max(0, price.basePriceCents) : null;
        const introInput = intros?.[packageId];
        // Only a VALIDATING intro prices anything; a half-typed one leaves the row
        // showing its settled price rather than flickering to a number that cannot
        // be saved.
        const introError = P.introError(introInput);
        const intro = introError ? undefined : P.parseIntro(introInput);
        // THE UNIFIED OFFER. Prefers what the builder holds; falls back to the
        // legacy pair so a deal opened from storage before it was re-saved still
        // shows the right numbers.
        const parsedDiscount = P.parseDiscount(discount);
        const offer = offers?.[packageId]
            || P.termToInput((0, dealOffer_1.offerFromPackageLegacy)({
                ...(parsedDiscount ? { discount: parsedDiscount } : {}),
                ...(intro ? { intro } : {}),
            }));
        const offerError = P.termError(offer, exports.PACKAGE_SHAPE);
        const parsedOffer = offerError ? undefined : P.parseTerm(offer, exports.PACKAGE_SHAPE);
        const amounts = (0, dealOffer_1.offerAmounts)(
        // A package bills monthly and has nothing up front.
        { monthlyCents: listCents ?? 0, oneTimeCents: 0 }, parsedOffer);
        return {
            packageId,
            name: record?.name || packageId,
            listCents,
            // The SETTLED price — what the package costs once any introductory
            // period is over. Deliberately unaffected by the promotion: `priceCents`
            // and `dealPackagesTotals` are settled figures, and a promotion that
            // moved them would misstate the deal's own price.
            netCents: listCents === null ? null : amounts.settledMonthlyCents,
            noBasePrice: !!record && typeof record.basePrice !== 'number',
            discount,
            // The offer is what an operator types, so its complaint comes first; the
            // legacy discount is still checked so a deal that carries only the old
            // field still reports a stored discount the server would refuse.
            error: offerError || P.discountError(discount),
            ...(introInput ? { intro: introInput } : {}),
            // The promotion, from the same `amounts`. A deal carrying only the legacy
            // `packageIntros` reaches this through `offerFromPackageLegacy` above, so
            // both shapes report one introductory period.
            introCents: amounts.promoMonths ? amounts.promoMonthlyCents : null,
            introMonths: amounts.promoMonths ?? null,
            introError,
            offer,
            ...(parsedOffer ? { parsedOffer } : {}),
            amounts,
            offerError,
        };
    });
}
/**
 * The deal's SOFTWARE lines as phased lines — what the price-phase model needs.
 *
 * A package with no introductory price is still a phased line; it simply has no
 * boundary of its own. Including it is what makes a phase total the whole
 * software bill rather than only the promoted part of it.
 */
function packagePhasedLines(rows) {
    const lines = [];
    for (const row of rows || []) {
        if (row.listCents === null)
            continue; // unpriced: contributes nothing
        // Through `offerPhasedLine`, so software and hardware reach the schedule
        // by the same route and one phase can hold both.
        lines.push((0, dealOffer_1.offerPhasedLine)({
            key: row.packageId,
            quantity: 1,
            list: { monthlyCents: row.listCents, oneTimeCents: 0 },
            // The term the row was already priced from — see `parsedOffer`. Identical
            // to the `offerError ? undefined : toOfferTerm(offer, PACKAGE_SHAPE)` this
            // used to recompute, evaluated once instead of twice.
            ...(row.parsedOffer ? { term: row.parsedOffer } : {}),
        }));
    }
    return lines;
}
/**
 * The deal's software price over time.
 *
 * Devices are deliberately EXCLUDED: a deal offers several finance options per
 * device and a customer takes one, so hardware belongs in the per-scenario
 * cards (`dealScenarios`) rather than in a single software timeline that would
 * have to pick an option on the customer's behalf.
 */
function dealPhaseView(rows, format) {
    const phases = (0, pricePhases_1.buildPricePhases)(packagePhasedLines(rows));
    const headline = (0, pricePhases_1.phaseHeadline)(phases);
    return {
        phases,
        disclosure: (0, pricePhases_1.phaseDisclosure)(phases, format),
        firstCents: headline.firstCents,
        settlesAtCents: headline.settlesAtCents,
        changes: headline.changes,
        peak: (0, pricePhases_1.phasePeak)(phases),
    };
}
/**
 * The packages total, summed from rows the caller ALREADY has.
 *
 * Exists so a screen showing per-package rows and a total under them cannot
 * compute the two separately: the tile is the sum of exactly the numbers above
 * it, by construction, rather than by two functions agreeing.
 *
 * A package with no catalog row is `missingPackageIds` and contributes nothing
 * — never $0.00 folded into the sum, which is the same rule
 * `dealPackagesTotals` follows on the server.
 */
function dealTotalsFromRows(rows) {
    const totals = { listCents: 0, discountCents: 0, netCents: 0, missingPackageIds: [] };
    for (const row of rows || []) {
        if (row.listCents === null) {
            totals.missingPackageIds.push(row.packageId);
            continue;
        }
        totals.listCents += row.listCents;
        totals.netCents += row.netCents ?? row.listCents;
    }
    totals.discountCents = totals.listCents - totals.netCents;
    return totals;
}
/**
 * One SKU as a choosable, DEAL-PRICED option.
 *
 * Prices come from `deviceDealTiers` at one unit — the same function the server
 * bills through — so a preview cannot show a figure the signup will not honour.
 */
function optionFor(sku, term, 
/** The unified offer, when the deal carries one. Its promotion is the only source of one. */
offer) {
    const priceable = {
        skuId: sku.skuId,
        amountCents: sku.amountCents,
        saleMode: sku.saleMode,
        ...(typeof sku.downPaymentCents === 'number'
            ? { downPaymentCents: sku.downPaymentCents }
            : {}),
    };
    const [tier] = (0, dealDevice_1.deviceDealTiers)({ sku: priceable, quantity: 1, term });
    const isOutright = sku.saleMode === 'outright';
    // The promotion, through the same `offerAmounts` the builder and the server
    // price from. Absent an offer this is the settled amount, so the card renders
    // one price and says nothing about a period that does not exist.
    const promo = offer ? (0, dealOffer_1.offerAmounts)((0, dealDevice_1.deviceListPricing)(priceable), offer) : undefined;
    const amountCents = tier ? (isOutright ? tier.oneTimeCents : tier.monthlyCents) : sku.amountCents;
    const listCents = tier ? (isOutright ? tier.listOneTimeCents : tier.listMonthlyCents) : sku.amountCents;
    const downPaymentCents = tier && !isOutright ? tier.oneTimeCents : 0;
    const listDownPaymentCents = tier && !isOutright ? tier.listOneTimeCents : 0;
    return {
        skuId: sku.skuId,
        tab: offerTabLabel(sku.saleMode),
        priceLabel: offerPriceLabel(sku, amountCents),
        ...(sku.saleMode ? { saleMode: sku.saleMode } : {}),
        amountCents,
        listAmountCents: listCents,
        downPaymentCents,
        listDownPaymentCents,
        // A saving on EITHER cadence counts: a "$0 down" offer against a SKU that
        // asks for $99 moves no monthly figure but is very much a discount.
        discounted: Boolean(tier?.fromDeal) && (listCents > amountCents || listDownPaymentCents > downPaymentCents),
        promoAmountCents: promo
            ? (isOutright ? promo.promoOneTimeCents : promo.promoMonthlyCents)
            : amountCents,
        ...(promo?.promoMonths ? { promoMonths: promo.promoMonths } : {}),
        scopeNote: offerScopeNote((offer ?? term)),
    };
}
/**
 * One device row: a bundle wins over a bare model, and a bundle's own
 * components — not the model catalog at large — are what the "contents" line
 * describes.
 */
function resolveDevice(sku, bundles, models) {
    const bundle = sku.bundleId ? bundles.find((b) => b.bundleId === sku.bundleId) : undefined;
    const model = sku.modelId ? models.find((m) => m.modelId === sku.modelId) : undefined;
    const name = bundle?.name || model?.modelName || sku.name;
    const heroImageUrl = bundle?.heroImageUrl || model?.media?.heroImageUrl || null;
    const contentsSummary = bundle && bundle.components.length > 0
        ? bundle.components
            .map((c) => {
            const componentModel = models.find((x) => x.modelId === c.modelId);
            const componentName = componentModel?.modelName || c.modelId;
            return c.quantity > 1 ? `${componentName} x${c.quantity}` : componentName;
        })
            .join(', ')
        : (bundle?.description || model?.description || '');
    return {
        name,
        contentsSummary,
        heroImageUrl,
        // One SKU is one payment option; the grouping in the model collects them.
        priceLabels: [],
        options: [],
        // Overwritten with the real grouping key by `buildOfferPresentation`; a
        // per-SKU value here keeps this helper's return type honest on its own.
        key: sku.skuId,
        priceLabel: offerPriceLabel(sku, sku.amountCents),
        missingImage: !heroImageUrl,
    };
}
/**
 * THE ONE MODEL every surface renders — the deal live view, the public landing
 * page, the affiliate page, an order's offer panel and the editor's preview.
 *
 * A straight port of super-admin-ui's `buildDealPreviewModel`. It reads
 * `OfferInputs` rather than a form (so a Lambda can call it), takes `priceCents`
 * already in cents rather than parsing a typed dollar string, reaches the
 * caller's term parser through `catalog.parseTerm`, and adds the promotional
 * WORDING the surfaces used to each invent for themselves. It changes no
 * number: every figure below still comes from `dealStrikethrough`,
 * `offerAmounts`, `deviceDealTiers` and `buildPricePhases`, unchanged.
 */
function buildOfferPresentation(inputs, catalog) {
    // --- Label: explicit ONLY ------------------------------------------------
    // Trials retired 2026-08-28. No label, blurb or badge may be derived from
    // `trialDays`; the surface must show what the customer will actually get.
    const label = (inputs.landingPageLabel || '').trim() || null;
    // --- Price + strikethrough — the SAME packages sum the editor's recompose
    // bar computes, fed straight into `dealStrikethrough`.
    const priceCents = inputs.priceCents;
    const packageRows = dealPackageRows(inputs.packageIds, catalog.packages, inputs.packageDiscounts, inputs.packageIntros, inputs.offerTerms, { ...(catalog.parsers || {}), parseTerm: catalog.parseTerm });
    const packagesUnpriced = packageRows.some((r) => r.listCents === null || r.noBasePrice);
    const totals = dealTotalsFromRows(packageRows);
    const packagesSumCents = packagesUnpriced ? null : totals.listCents;
    const packagesNetCents = packagesUnpriced ? null : totals.netCents;
    const strikethrough = (0, deal_1.dealStrikethrough)(packagesSumCents, priceCents);
    // The software's own schedule — the limited-time price and when it ends.
    const softwareSchedule = dealPhaseView(packageRows, (cents) => `$${(cents / 100).toFixed(2)}`);
    // --- Features: selected packages, in SELECTION order, with catalog names.
    // Empty selection falls back to the SAME five static bullets the live card
    // falls back to — never an empty list, which is not what a customer sees.
    const packageById = new Map((catalog.packages || []).filter((p) => p?.id).map((p) => [p.id, p]));
    const selectedFeatures = Array.from(new Set(inputs.packageIds || [])).map((id) => {
        const pkg = packageById.get(id);
        return {
            id,
            name: pkg?.name || id,
            ...(pkg?.icon ? { icon: pkg.icon } : {}),
            ...(pkg?.color ? { color: pkg.color } : {}),
            ...(pkg?.colorType ? { colorType: pkg.colorType } : {}),
            ...(pkg?.gradientFrom ? { gradientFrom: pkg.gradientFrom } : {}),
            ...(pkg?.gradientTo ? { gradientTo: pkg.gradientTo } : {}),
        };
    });
    const featuresAreFallback = selectedFeatures.length === 0;
    const features = featuresAreFallback
        ? exports.DEFAULT_OFFER_FEATURES.map((name, i) => ({ id: `fallback-${i}`, name }))
        : selectedFeatures;
    // --- The software side, package by package, with its discount in words ---
    const packageLines = packageRows.map((row) => {
        const pkg = packageById.get(row.packageId);
        return {
            id: row.packageId,
            name: row.name,
            ...(pkg?.icon ? { icon: pkg.icon } : {}),
            ...(pkg?.color ? { color: pkg.color } : {}),
            ...(pkg?.colorType ? { colorType: pkg.colorType } : {}),
            ...(pkg?.gradientFrom ? { gradientFrom: pkg.gradientFrom } : {}),
            ...(pkg?.gradientTo ? { gradientTo: pkg.gradientTo } : {}),
            listCents: row.listCents,
            netCents: row.netCents,
            // The operator's own percentage wins when they stated one — that is the
            // number the campaign was written around.
            discountLabel: (row.listCents === null || row.netCents === null)
                ? null
                : offerDiscountLabel({
                    listCents: row.listCents,
                    netCents: row.netCents,
                    ...(typeof row.parsedOffer?.discount?.percentOff === 'number'
                        ? { percentOff: row.parsedOffer.discount.percentOff }
                        : {}),
                }),
        };
    });
    // --- Devices: selected sku ids, in selection order. A sku the billing ----
    // catalog no longer lists resolves to nothing here — there is no photo or
    // price to show a customer for it.
    const skuById = new Map((catalog.deviceSkus || []).map((s) => [s.skuId, s]));
    // Grouped by the DEVICE, not the SKU: `bundleId`/`modelId` identify the
    // hardware, and the several finance SKUs against it are its payment options.
    const resolved = Array.from(new Set(inputs.deviceSkuIds || []))
        .map((id) => skuById.get(id))
        .filter((sku) => !!sku)
        .map((sku) => ({ sku, device: resolveDevice(sku, catalog.bundles || [], catalog.models || []) }));
    const deviceByKey = new Map();
    const devices = [];
    for (const { sku, device } of resolved) {
        const key = sku.bundleId || sku.modelId || device.name || sku.skuId;
        // The UNIFIED OFFER wins outright and never merges; the legacy
        // `deviceTerms` is the fallback for a deal saved before the unification.
        // `parseTerm` is where a half-typed term becomes `undefined`, leaving the
        // option at list rather than flickering to a number nobody can save.
        const shape = (0, exports.deviceShape)(sku.saleMode);
        const offer = catalog.parseTerm(inputs.offerTerms?.[sku.skuId], shape);
        const legacyTerm = catalog.parseTerm(inputs.deviceTerms?.[sku.skuId], shape);
        const option = optionFor(sku, offer ?? legacyTerm, offer);
        const existing = deviceByKey.get(key);
        if (existing) {
            if (option.priceLabel)
                existing.priceLabels.push(option.priceLabel);
            existing.options.push(option);
            continue;
        }
        const entry = {
            ...device,
            key,
            priceLabel: option.priceLabel,
            priceLabels: option.priceLabel ? [option.priceLabel] : [],
            options: [option],
        };
        deviceByKey.set(key, entry);
        devices.push(entry);
    }
    // --- The wording, from Task 1's helpers ----------------------------------
    const savingCents = (strikethrough.show && priceCents !== null)
        ? Math.max(0, strikethrough.wasCents - priceCents) : 0;
    /*
      HOW LONG THE OPENING PRICE RUNS. Not a new number: `phases[0].iterations` is
      how many cycles the first phase bills for, and a schedule with a second
      phase is one whose opening price ends. `buildPricePhases` set it; this only
      reads it.
  
      ⚠️ `iterations` counts BILLING CYCLES, not calendar months. Reading it
      straight into `promoMonths` — which `offerPromoDurationLabel` renders as
      the word "months" — is correct today only because `PACKAGE_SHAPE` makes
      every package bill monthly, so a cycle and a month are the same thing. If
      a package ever bills on a different interval, this label would keep saying
      "for 12 months" about twelve years. Not fixed here: this is a comment
      only, see the task that added it.
    */
    const promoMonths = softwareSchedule.phases.length > 1
        ? softwareSchedule.phases[0].iterations
        : undefined;
    /*
      ⚠️ TWO BASES, ONE OBJECT. `monthlyCents` is the DEAL PRICE (authoritative at
      signup, spec §12/A2); `firstMonthlyCents`, `promoMonths` and
      `promoDurationLabel` come from `softwareSchedule` — the PACKAGE SUM net of
      its own offer terms. They agree only while `priceCents` equals that sum.
      See the block comment on `OfferHeadline` before changing or leading with any
      of them. NOT arithmetic to adjust here: this is a verbatim port of
      `dealPreviewModel.ts:504-508`.
    */
    const headline = {
        monthlyCents: priceCents,
        wasCents: strikethrough.show ? strikethrough.wasCents : null,
        savingCents,
        savingLabel: offerSavingLabel(savingCents),
        firstMonthlyCents: softwareSchedule.phases.length ? softwareSchedule.firstCents : priceCents,
        ...(promoMonths ? { promoMonths } : {}),
        promoDurationLabel: offerPromoDurationLabel(promoMonths),
    };
    return {
        code: inputs.code,
        name: inputs.name,
        label,
        blurb: (inputs.landingPageDescription || '').trim() || exports.DEFAULT_OFFER_BLURB,
        isDefaultDeal: !!inputs.isDefaultDeal,
        headline,
        software: {
            listCents: packagesSumCents,
            netCents: packagesNetCents,
            packages: packageLines,
            lines: packagePhasedLines(packageRows),
            firstCents: softwareSchedule.firstCents,
            settlesAtCents: softwareSchedule.settlesAtCents,
            changes: softwareSchedule.changes,
        },
        features,
        featuresAreFallback,
        devices,
        phases: softwareSchedule.phases,
        disclosure: softwareSchedule.disclosure,
    };
}
//# sourceMappingURL=offerPresentation.js.map